import React, { useMemo, useState, useEffect } from "react";
import { RouteSegmentsTable } from "./route-points-table";
import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@racoon/ui";
import { PlaybackInfo } from "./playback-info";
import { TimelinePlayer } from "@racoon/ui";
import { GpsTrackingPoint, TimelineSource, TruckWithShifts } from "../model/types";
import type { RouteTimelineSegment } from "@racoon/ui";
import { GpsMapView } from "./gps-map-view";
import type { MapObject } from "~/features/map-view/ui/map-view";
import { useMapTrucks, useRoutePaths, useMapCenter } from "../model/useMapData";
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from "../lib/constants";
import { getLastPositionByVehicleId } from "../lib/utils";
import { ConnectProto } from "@racoon/api";
import { useT, Namespace } from "@racoon/i18n";
import type { TimelinePoint, TimelineSegment } from "@racoon/ui";
import { Check, ChevronDown, Minimize2 } from "lucide-react";
import { IcNavigation, IcExpandMap, IcMapLayers } from "@racoon/icons";
import { GpsStopsLegend, useGpsStopsLayer } from "~/features/gps-stops";
import { PhotoPointsToggle } from "~/features/map-view/components/PhotoPointsLayer";
import { photoPointsUpTo, type PhotoPoint } from "~/features/map-view/model/helpers/photo-points";
import { useFullscreen } from "~/shared/hooks/useFullscreen";

interface MapViewSectionProps {
  viewMode: "map" | "table";
  onViewModeChange: (mode: "map" | "table") => void;
  gpsPoints: GpsTrackingPoint[];
  /**
   * Time span of the full track, unaffected by playback truncation. Used as the
   * GPS-stops fetch window so it stays stable while playback runs. Falls back to
   * the span of `gpsPoints` when omitted.
   */
  trackWindow?: { from: Date | null; to: Date | null };
  selectedVehicle?: TruckWithShifts | null;
  selectedPointId?: string;
  onPointClick?: (point: GpsTrackingPoint) => void;
  loading?: boolean;
  /** The track request failed: the empty map says so rather than "no data". */
  trackError?: boolean;
  pickupLocations?: ConnectProto.PickupLocation[];
  /** The routes' jobs as numbered step pins, drawn in place of `pickupLocations`. */
  stepPins?: MapObject[];
  depots?: ConnectProto.Location[];
  playbackPosition?: { lat: number; lon: number; speed: number; angle?: number } | null;
  playbackTime?: number | null;
  timelinePoints?: TimelinePoint[];
  timelineSegments?: TimelineSegment[];
  tableSegments?: RouteTimelineSegment[];
  onTimeChange?: (time: number) => void;
  hasVehicles?: boolean;
  isPlaybackActive?: boolean;
  timelineSource?: TimelineSource;
  onTimelineSourceChange?: (source: TimelineSource) => void;
  initialPlaybackTime?: number | null;
  /** Where the selected truck's drivers took their service photos. */
  photoPoints?: PhotoPoint[];
  photosOpenReport?: boolean;
  /**
   * A host owns the first slot of the top-right corner (the collapsible panel's
   * toggle), so the map's control stack starts one slot lower and the table
   * view's controls keep clear of it.
   */
  reserveTopRightSlot?: boolean;
  /** The time / speed / coordinates box over the map while playing. */
  showPlaybackInfo?: boolean;
  /**
   * A collapsed panel's strip: the tabs and the player over the map are hidden
   * (kept mounted, so playback keeps its place), and the control stack moves to
   * the strip's edge — the left one, which is the part of the map it shows.
   */
  hideChrome?: boolean;
  /** No frame of its own: the host lays the map flush against its edges (the TTN page). */
  flush?: boolean;
}

const TIMELINE_SOURCE_LABEL_KEYS: Record<TimelineSource, "sourceAll" | "sourceGps" | "sourceFact"> =
  {
    all: "sourceAll",
    gps: "sourceGps",
    fact: "sourceFact",
  };

const TIMELINE_SOURCES: TimelineSource[] = ["all", "gps", "fact"];

export const MapViewSection: React.FC<MapViewSectionProps> = ({
  viewMode,
  onViewModeChange,
  gpsPoints,
  trackWindow,
  selectedVehicle,
  selectedPointId,
  onPointClick,
  loading = false,
  trackError = false,
  pickupLocations = [],
  stepPins,
  depots = [],
  playbackPosition = null,
  playbackTime = null,
  timelinePoints = [],
  timelineSegments = [],
  tableSegments = [],
  onTimeChange,
  hasVehicles = false,
  isPlaybackActive = false,
  timelineSource = "all",
  onTimelineSourceChange,
  initialPlaybackTime = null,
  photoPoints = [],
  photosOpenReport,
  reserveTopRightSlot = false,
  showPlaybackInfo = true,
  hideChrome = false,
  flush = false,
}) => {
  const t = useT();
  const { isFullscreen, toggleFullscreen, elementRef } = useFullscreen();
  const fullscreenLabel = t(Namespace.Common, isFullscreen ? "exitFullscreen" : "enterFullscreen");
  // Only the selected vehicle has GPS points, and it is what carries the truck
  // type the marker sprite is picked by.
  const mapVehicles = useMemo(() => (selectedVehicle ? [selectedVehicle] : []), [selectedVehicle]);
  const trucks = useMapTrucks(gpsPoints, mapVehicles);
  const baseRoutePaths = useRoutePaths(gpsPoints);
  const fitPath = useMemo(
    () => timelinePoints.map((p) => [p.lat, p.lon] as [number, number]),
    [timelinePoints],
  );
  // A Truck carries no coordinates, so the map centers on the latest GPS point.
  const center = useMapCenter(null, gpsPoints, DEFAULT_MAP_CENTER);
  const [autoFollow, setAutoFollow] = useState(true);

  // Scope the stops to the window the map is actually showing rather than the
  // whole picked day: the points here are already narrowed by driver group and
  // time filter, and stops outside that window would not line up with the path.
  //
  // Prefer the caller's `trackWindow`, which spans the full track. Deriving the
  // window from `gpsPoints` is only a fallback, because during playback that
  // array is truncated at the current time and the window would move on every
  // tick, refetching the stops each time.
  const fallbackWindow = useMemo(() => {
    if (gpsPoints.length === 0) return { from: null, to: null };
    let min = gpsPoints[0].timestamp.getTime();
    let max = min;
    for (const point of gpsPoints) {
      const time = point.timestamp.getTime();
      if (time < min) min = time;
      if (time > max) max = time;
    }
    return { from: new Date(min), to: new Date(max) };
  }, [gpsPoints]);

  const gpsWindow = trackWindow ?? fallbackWindow;

  const gpsStops = useGpsStopsLayer({
    truckId: selectedVehicle?.id,
    ownerOrganizationId: selectedVehicle?.ownerOrganizationId,
    from: gpsWindow.from,
    to: gpsWindow.to,
    // The sites of the routes shown: a stop on one of them is not suspicious.
    plannedSites: pickupLocations,
  });

  // Reveal each stop as the truck reaches it, so the markers appear along with
  // the emerging track instead of all at once. Purely a display filter over the
  // single fetch above -- scrubbing back and forth costs no requests.
  const visibleStops = useMemo(() => {
    if (!isPlaybackActive || playbackTime === null) return gpsStops.layerProps.stops;
    return gpsStops.layerProps.stops.filter((stop) => stop.startAt.getTime() <= playbackTime);
  }, [gpsStops.layerProps.stops, isPlaybackActive, playbackTime]);

  const gpsStopsLayerProps = useMemo(
    () => ({ ...gpsStops.layerProps, stops: visibleStops }),
    [gpsStops.layerProps, visibleStops],
  );

  const [showPhotos, setShowPhotos] = useState(true);
  const visiblePhotos = useMemo(() => {
    if (!showPhotos) return [];
    if (!isPlaybackActive || playbackTime === null) return photoPoints;
    return photoPointsUpTo(photoPoints, playbackTime);
  }, [photoPoints, showPhotos, isPlaybackActive, playbackTime]);

  // Reset auto-follow when playback becomes active
  useEffect(() => {
    if (isPlaybackActive) {
      setAutoFollow(true);
    }
  }, [isPlaybackActive]);

  // Extend route path with current playback position so the path connects to the vehicle marker
  const routePaths = useMemo(() => {
    if (!isPlaybackActive || !playbackPosition || !selectedVehicle) {
      return baseRoutePaths;
    }

    return baseRoutePaths.map((route) => {
      if (route.vehicleId === String(selectedVehicle.id)) {
        return {
          ...route,
          path: [...route.path, [playbackPosition.lat, playbackPosition.lon] as [number, number]],
        };
      }
      return route;
    });
  }, [baseRoutePaths, isPlaybackActive, playbackPosition, selectedVehicle]);

  const handleMarkerClick = (item: { id: number | string; name: string }) => {
    const latestPoint = getLastPositionByVehicleId(gpsPoints, String(item.id));

    if (latestPoint && onPointClick) {
      onPointClick(latestPoint);
    }
  };

  const showPlayer =
    viewMode === "map" && !!selectedVehicle && timelinePoints.length > 0 && !!onTimeChange;

  return (
    <div
      ref={elementRef}
      className={cn(
        "relative flex flex-col bg-white",
        !flush && "rounded-xl",
        isFullscreen ? "h-screen w-screen" : "h-full",
      )}
    >
      {viewMode === "map" && (
        <div
          className={cn(
            "absolute inset-0 z-0",
            !flush && "border rounded-xl",
            // Tucked into the player's label row, bottom-aligned with the
            // end-time chip. Leaflet's own 10px control margin is zeroed so
            // these offsets are exact.
            showPlayer &&
              "[&_.leaflet-control-attribution]:!m-0 [&_.leaflet-control-attribution]:!leading-[17px] [&_.leaflet-bottom.leaflet-right]:mb-[51px] [&_.leaflet-bottom.leaflet-right]:mr-[85px]",
          )}
        >
          {!selectedVehicle ? (
            hasVehicles ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                {t(Namespace.GpsTracking, "selectVehicleForRoute")}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                {t(Namespace.GpsTracking, "noGpsData")}
              </div>
            )
          ) : gpsPoints.length > 0 ? (
            <GpsMapView
              center={center}
              zoom={DEFAULT_MAP_ZOOM}
              trucks={trucks}
              routePaths={routePaths}
              selectedVehicleId={String(selectedVehicle.id)}
              selectedPointId={selectedPointId}
              onMarkerClick={handleMarkerClick}
              onPointClick={onPointClick}
              gpsPoints={gpsPoints}
              pickupLocations={pickupLocations}
              stepPins={stepPins}
              depots={depots}
              playbackPosition={playbackPosition}
              autoFollow={autoFollow}
              onAutoFollowChange={setAutoFollow}
              gpsStops={gpsStopsLayerProps}
              photoPoints={visiblePhotos}
              photosOpenReport={photosOpenReport}
              fitPath={fitPath}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              {t(
                Namespace.GpsTracking,
                loading ? "loadingGPS" : trackError ? "errorLoadingGPS" : "noGpsData",
              )}
            </div>
          )}
        </div>
      )}

      {/* Map controls stack down the corner, one 32px slot each. */}
      {viewMode === "map" && (
        <div
          className={cn(
            "absolute z-20 flex flex-col gap-2",
            reserveTopRightSlot ? "top-12" : "top-2",
            // The collapsed panel makes the map inert; its controls stay usable.
            hideChrome ? "pointer-events-auto left-2" : "right-2",
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleFullscreen}
            title={fullscreenLabel}
            aria-label={fullscreenLabel}
            className="max-h-8 max-w-8 bg-white shadow-md hover:bg-gray-100"
          >
            {/* Same glyphs and sizing as the MapWithGeofence fullscreen toggle. */}
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4 text-[#151A24]" />
            ) : (
              <IcExpandMap className="h-8 w-8 text-[#151A24]" />
            )}
          </Button>

          {selectedVehicle && gpsPoints.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  title={t(Namespace.Common, "mapLayers")}
                  aria-label={t(Namespace.Common, "mapLayers")}
                  className="max-h-8 max-w-8 bg-white shadow-md hover:bg-gray-100"
                >
                  <IcMapLayers className="h-5 w-5 text-brand-blue" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-max min-w-72 max-w-[24rem] p-4">
                <GpsStopsLegend {...gpsStops.legendProps} idPrefix="gps-tracking-stops" />
                {photoPoints.length > 0 && (
                  <div className="mt-3 border-t border-gray-200 pt-3">
                    <PhotoPointsToggle
                      enabled={showPhotos}
                      onEnabledChange={setShowPhotos}
                      points={photoPoints}
                      idPrefix="gps-tracking-photos"
                    />
                  </div>
                )}
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}

      {/* The right padding keeps the row clear of the control stack (map view) or
          of the host's corner slot (table view). */}
      <div
        className={cn(
          "relative z-10 px-2 pt-2 flex-shrink-0 flex justify-between gap-4",
          (viewMode === "map" || reserveTopRightSlot) && "pr-12",
          hideChrome && "invisible",
        )}
      >
        <Tabs
          value={viewMode}
          onValueChange={(v) => onViewModeChange(v as "map" | "table")}
          className="flex-1"
        >
          <TabsList>
            <TabsTrigger value="map">{t(Namespace.GpsTracking, "mapView")}</TabsTrigger>
            <TabsTrigger value="table">{t(Namespace.GpsTracking, "tableView")}</TabsTrigger>
          </TabsList>
        </Tabs>

        {showPlaybackInfo &&
          viewMode === "map" &&
          playbackPosition &&
          playbackTime !== null &&
          isPlaybackActive && (
            <PlaybackInfo
              currentTime={playbackTime}
              lat={playbackPosition.lat}
              lon={playbackPosition.lon}
              speed={playbackPosition.speed}
              angle={playbackPosition.angle}
              className="shrink-0"
            />
          )}

        {viewMode === "table" && onTimelineSourceChange && (
          <div className="flex flex-1 justify-end mb-2 items-start">
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2 px-2 h-10 bg-white border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                  {t(Namespace.GpsTracking, TIMELINE_SOURCE_LABEL_KEYS[timelineSource])}
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[10rem] !p-2">
                <div className="space-y-1">
                  {TIMELINE_SOURCES.map((source) => (
                    <button
                      key={source}
                      onClick={() => onTimelineSourceChange(source)}
                      className={cn(
                        "w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-md transition-colors",
                        timelineSource === source
                          ? "bg-brand-blue/10 text-brand-blue"
                          : "text-gray-700 hover:bg-gray-100",
                      )}
                    >
                      {t(Namespace.GpsTracking, TIMELINE_SOURCE_LABEL_KEYS[source])}
                      {timelineSource === source && <Check className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Timeline Player - positioned at bottom of map */}
      {showPlayer && (
        <div className={cn("absolute bottom-2 left-2 right-2 z-20", hideChrome && "invisible")}>
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setAutoFollow(!autoFollow)}
              className={cn(
                "h-10 w-10 flex items-center justify-center rounded-lg border transition-colors",
                autoFollow
                  ? "bg-white border-brand-blue text-brand-blue"
                  : "bg-white border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300",
              )}
              title="Follow vehicle"
            >
              <IcNavigation className="h-5 w-5" />
            </button>
          </div>
          <TimelinePlayer
            points={timelinePoints}
            segments={timelineSegments}
            onTimeChange={onTimeChange}
            initialTime={initialPlaybackTime}
          />
        </div>
      )}

      <div
        className={`relative z-10 flex-1 min-h-0 flex flex-col ${viewMode === "map" ? "pointer-events-none" : ""}`}
      >
        <Tabs
          value={viewMode}
          onValueChange={(v) => onViewModeChange(v as "map" | "table")}
          className="flex-1 flex flex-col min-h-0"
        >
          <TabsContent
            value="table"
            className="m-0 pointer-events-auto flex-1 data-[state=active]:flex flex-col min-h-0 px-4 pb-4 bg-white"
          >
            <RouteSegmentsTable
              data={tableSegments}
              loading={loading}
              showSource={timelineSource === "all"}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
